import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card, CardBody, CardHeader, Row, Col, Container,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
const TemperatureAlarm = () => {
   const location = useLocation();
    const navigate = useNavigate();
    const name = location.state?.name;
    const id = location.state?.id;
  const [tempData, setTempData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [machines, setMachines] = useState([]);

  // ✅ selectedMachineId → used for API calls
  // ✅ selectedMachineName → used for display only
  const [selectedMachineId, setSelectedMachineId] = useState(id || "");
  const [selectedMachineName, setSelectedMachineName] = useState(name || "");

  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const fetchMachines = async () => {
    try {
      const res = await axios.get(
        "https://api.hanoch.in/api/machines/by-company/?id=6"
      );
      const machineList = res.data || [];
      setMachines(machineList);

      // ✅ If id & name passed from navigation → use them
      if (id && name) {
        setSelectedMachineId(id);
        setSelectedMachineName(name);
      }
      // ✅ else default to first machine in list
      else if (machineList.length > 0) {
        setSelectedMachineId(machineList[0].id);
        setSelectedMachineName(machineList[0].name);
      }
    } catch (err) {
      toast.error("Failed to load machines");
    }
  };

  useEffect(() => {
    fetchMachines();
  }, []);

  useEffect(() => {
    if (!selectedMachineName || !selectedDate) return;

    fetchTemperatureTrend();

    const intervalId = setInterval(() => {
      fetchTemperatureTrend();
    }, 60 * 600);

    return () => clearInterval(intervalId);
  }, [selectedMachineName, selectedDate]);

  const fetchTemperatureTrend = async () => {
    try {
      setLoading(true);

      // ✅ Use selectedMachineName for API call
      const res = await axios.get(
        `https://api.hanoch.in/api/ptc/telemetry/?machine=${selectedMachineName}&date=${selectedDate}`
      );

      const cleanedData = (res.data?.results || res.results || []).map((item) => ({
        ...item,
        temperature: item?.temperature === 0 ? null : item?.temperature,
        machine_status: item?.machine_status === 0 ? null : item?.machine_status,
      }));

      setTempData(cleanedData);
    } catch (err) {
      toast.error("Failed to load temperature trend");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle dropdown change — update both id and name together
 // ✅ Handle dropdown change — value is name, find by name
const handleMachineChange = (e) => {
  const selectedName = e.target.value;
  const selectedObj = machines.find((m) => m.name === selectedName);
  if (selectedObj) {
    setSelectedMachineId(selectedObj.id);
    setSelectedMachineName(selectedObj.name);
  }
};
  const exportToExcel = () => {
    if (!tempData.length) {
      toast.warning("No data to export");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(tempData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Temperature");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

    // ✅ Use name for readable filename
    saveAs(blob, `Temperature_${selectedMachineName}_${selectedDate}.xlsx`);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="OptiMize" breadcrumbItem="Temperature Trend" />

          <Row>
            <div>{selectedMachineName}</div>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Temperature vs Time</h5>
                  <Row>
                    <Col lg={4}>
                      <select
                        className="form-select"
                        value={selectedMachineName}        // ✅ value = id
                        onChange={handleMachineChange}   // ✅ updates both id & name
                      >
                        {machines.map((m) => (
                          <option key={m.id} value={m.name}>  {/* ✅ value = id, label = name */}
                            {m.name}
                          </option>
                        ))}
                      </select>
                    </Col>
                    <Col lg={4}>
                      <input
                        type="date"
                        className="form-control"
                        value={selectedDate}
                        max={new Date().toISOString().split("T")[0]}
                        onChange={(e) => setSelectedDate(e.target.value)}
                      />
                    </Col>
                    <Col lg={4}>
                      <button
                        className="btn btn-outline-success"
                        onClick={exportToExcel}
                      >
                        Export Excel
                      </button>
                    </Col>
                  </Row>
                </CardHeader>

                <CardBody style={{ height: 350 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={tempData}>
                      <CartesianGrid stroke="#e0e0e0" strokeDasharray="3 3" />
                      <XAxis
                        dataKey="time"
                        interval={59}
                        tickFormatter={(t) => t.slice(0, 5)}
                        axisLine={{ stroke: "#aaa" }}
                        tickLine={true}
                      />
                      <YAxis axisLine={{ stroke: "#aaa" }} tickLine={true} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="temperature"
                        dot={false}
                        strokeWidth={2}
                        connectNulls={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TemperatureAlarm;